// Libs
import classNames from 'classnames/bind';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Typography } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseInput, Loading } from '~/components/common';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { defaultValuesForm, schema } from '~/pages/public/login/helper';
import { IFormLogin } from '~/utils/interface/auth';
import { authRouteAbsolute } from '~/utils/routes/auth';
import { RoleEnum } from '~/utils/enum/common';
import { StorageEnum } from '~/utils/enum/base';
import { generateGuid } from '~/utils/helper/common';
import { handleLogin } from '~/thunks/auth/authThunk';
import { authActions } from '~/thunks/auth/authSlice';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
// Styles, images, icons
import styles from './Login.module.scss';

const cx = classNames.bind(styles);

const Login = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/auth']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IFormLogin>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleNavigateToForgotPassword = () => {
    navigate(authRouteAbsolute.forgotPassword);
  };

  const handleSubmitFormSignIn = (data: IFormLogin) => {
    setIsLoading(true);

    dispatch(handleLogin(data))
      .unwrap()
      .then((res) => {
        const { account, token } = res?.data;
        localStorage.setItem(StorageEnum.ACCESS_TOKEN, token.access);
        localStorage.setItem(StorageEnum.REFRESH_TOKEN, token.refresh);
        localStorage.setItem(StorageEnum.ROLE, JSON.stringify(account.role));
        localStorage.setItem(StorageEnum.ACCOUNT_ID, account.id);
        localStorage.setItem(StorageEnum.DEVICE_ID, generateGuid());

        dispatch(authActions.setAccessToken(token.access));

        switch (account.role[0]) {
          case RoleEnum.SUPER_ADMIN:
            navigate(adminRouteAbsolute.dashboard);
            break;
          case RoleEnum.SALES_ADMIN:
            navigate(salesAdminRouteAbsolute.dashboard);
            break;
          case RoleEnum.SALES_PERSON:
            navigate(salesPersonRouteAbsolute.dashboard);
            break;
          case RoleEnum.OPERATIONS_MANAGER:
            navigate(operationsManagerRouteAbsolute.dashboard);
            break;
          case RoleEnum.WAREHOUSE_LEAD:
            navigate(warehouseLeadRouteAbsolute.dashboard);
            break;
          case RoleEnum.WAREHOUSE_STAFF:
            navigate(warehouseStaffRouteAbsolute.dashboard);
            break;
          case RoleEnum.FOREMAN:
            navigate(foremanRouteAbsolute.dashboard);
            break;
          case RoleEnum.CREW:
            navigate(crewRouteAbsolute.dashboard);
            break;
        }
        reset();
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };
  //#endregion Handle Function
  return (
    <Box id='loginPage' className={cx('container')} component={'form'} onSubmit={handleSubmit(handleSubmitFormSignIn)}>
      <Stack direction={'column'} spacing={'5px'} alignItems={'center'}>
        <Typography className={cx('pageName')}>{t('login_title')}</Typography>

        <Typography className={cx('description')}>{t('login_description')}</Typography>
      </Stack>

      <Stack direction={'column'} spacing={'20px'} marginTop={'10px'}>
        <Controller
          name='email'
          control={control}
          render={({ field: { value, onChange } }) => (
            <BaseInput
              height={52}
              borderRadius={'6px'}
              label={t('login_label_email_or_phone')}
              placeholder={t('login_placeholder_email_or_phone')}
              value={value}
              onChange={onChange}
              errorMessage={errors?.email?.message}
            />
          )}
        />

        <Controller
          name='password'
          control={control}
          render={({ field: { value, onChange } }) => (
            <BaseInput
              type='password'
              height={52}
              borderRadius={'6px'}
              label={t('login_label_password')}
              placeholder={t('login_placeholder_password')}
              value={value}
              onChange={onChange}
              errorMessage={errors?.password?.message}
            />
          )}
        />

        <Button variant='contained' className={cx('buttonLogin')} type='submit' size='large'>
          {t('login_button_login')}
        </Button>
      </Stack>

      <Stack direction={'row'} justifyContent={'center'}>
        <Typography component={'span'} className={cx('buttonForgot')} onClick={handleNavigateToForgotPassword}>
          {t('login_button_forgot_password')}
        </Typography>
      </Stack>

      <Loading loading={isLoading} />
    </Box>
  );
};

export default Login;
